// Centralized Vercel Analytics helpers for use across the app.
// Docs: https://vercel.com/docs/analytics/custom-events

/** Vercel Analytics accepts string/number/boolean/null primitives as properties. */
export type AnalyticsEventProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface OutboundLinkProperties extends AnalyticsEventProperties {
  platform?: string;
  location?: string;
}

export interface CTAProperties extends AnalyticsEventProperties {
  section?: string;
  location?: string;
}

// Only import track on the client to avoid SSR side-effects
let trackFn: (
  name: string,
  props?: AnalyticsEventProperties
) => void = () => {};

try {
  // eslint-disable-next-line global-require
  const { track } = require('@vercel/analytics');
  trackFn = track;
} catch {
  // noop on server
}

/**
 * Track a custom analytics event with optional properties.
 * @param name - Event name (e.g., 'contact_submit', 'cta_click')
 * @param props - Optional event properties
 */
export function trackEvent(
  name: string,
  props?: AnalyticsEventProperties
): void {
  if (!name || typeof name !== 'string') return;
  try {
    trackFn(name, props || {});
  } catch {
    // swallow analytics errors
  }
}

/** Convenience tracker for outbound link clicks. */
export function trackOutbound(
  url: string,
  props?: OutboundLinkProperties
): void {
  trackEvent('outbound_click', { url, ...props });
}

/** Convenience tracker for CTA button clicks. */
export function trackCta(label: string, props?: CTAProperties): void {
  trackEvent('cta_click', { label, ...props });
}
